import type { DatePickerDateRange } from '@/core/features/date-picker/date-picker-types';
import type { ReservationType } from '@/features/school-product-tiles-v2/school-product-tiles-v2-type';

import React from 'react';

import logger from '@app/core-features/logger/logger';

import CounterDesktop from '@/core/components/counter/desktop/counter-desktop';
import DividerHorizontalDesktop from '@/core/components/divider/divider-horizontal-desktop';
import { ButtonWrapper, DatePickerHorizontal, HtmlTextWrapper, Modal } from '@/core/features';
import { getFormatedDateShort } from '@/core/features/date/date';
import DatePickerVertical from '@/core/features/date-picker/date-picker-vertical/date-picker-vertical';
import DatePickerVerticalHeader from '@/core/features/date-picker/date-picker-vertical/date-picker-vertical-header';
import { centsToPriceString } from '@/core/features/price/price';
import color from '@/core/features/styles/color';
import useCart from '@/features/cart/service/use-cart';
import CheckoutCancellationInfoDesktop from '@/features/checkout/checkout-shared-components/checkout-cancellation-info-desktop';
import { useSchoolProductAvailability } from '@/features/school-product-availability/use-school-product-availability';

import styles from './school-reservation-overlay-desktop.module.scss';

type SchoolReservationOverlayProps = {
    activitySpotId: number;
    htmlHeaderText: null | string;
    onClose: () => void;
    productId: string;
    productName: string;
    schoolId: number;
    schoolName: string;
    type: ReservationType;
};

export default function SchoolReservationOverlay({
    activitySpotId,
    htmlHeaderText,
    onClose,
    productId,
    productName,
    schoolId,
    schoolName,
    type,
}: SchoolReservationOverlayProps) {
    const [count, setCount] = React.useState(0);
    const [isClosePending, setClosePending] = React.useState(false);
    const { addItem, isLoading: isCartLoading } = useCart({ activitySpotId, schoolId });

    const {
        cancellation,
        countLimits,
        datePickerRange,
        disabledDays,
        earliestBookableDate,
        getLastBookableDate,
        isLoading: isAvailabilityLoading,
        offerId,
        selectedDateRange,
        unitPrice,
        updateSelectedStartDate,
    } = useSchoolProductAvailability({
        productId,
        schoolId,
        type,
    });

    const ctaDisabled = isAvailabilityLoading || (isClosePending && isCartLoading);

    React.useEffect(() => {
        if (count < countLimits.min) {
            setCount(countLimits.min);
        } else if (count > countLimits.max) {
            setCount(countLimits.max);
        }
    }, [count, countLimits.max, countLimits.min]);

    React.useEffect(() => {
        if (isClosePending && !isCartLoading) {
            onClose();
        }
    }, [isCartLoading, isClosePending, onClose]);

    const totalPrice = React.useMemo(() => {
        const totalCents = unitPrice * count;
        return centsToPriceString(totalCents);
    }, [count, unitPrice]);

    const handleDayCountDecrease = () => {
        setCount((prev) => prev - 1);
    };

    const handleDayCountIncrease = () => {
        setCount((prev) => prev + 1);
    };

    const getCtaDateText = (selectedDate?: DatePickerDateRange) => {
        if (!selectedDate?.from) {
            return;
        }

        const fromDate = getFormatedDateShort(selectedDate.from);
        const toDate = selectedDate.to ? getFormatedDateShort(selectedDate.to) : '';
        return `${fromDate} - ${toDate}`;
    };

    const onCtaClick = () => {
        if (ctaDisabled) {
            return;
        }

        if (!offerId || !selectedDateRange?.from || !selectedDateRange.to) {
            logger.error(
                `Undefined values while processing reservation: offerId (${offerId}) and/or selectedDateRange (${JSON.stringify(
                    selectedDateRange,
                )})`,
            );
            return;
        }

        addItem({
            cancellation,
            count: {
                ...countLimits,
                current: count,
            },
            endDate: selectedDateRange.to,
            offerId,
            productId,
            productName,
            startDate: selectedDateRange.from,
            unitPrice,
        });
        setClosePending(true);
    };

    return (
        <>
            <Modal
                hasSmallOverlay={true}
                maxWidth={835}
                onClose={onClose}
                padding={'padding-20'}
            >
                <div className={styles.header}>
                    <div className={'font-size-18 bold'}>{productName}</div>
                    <div className={'font-size-16'}>{schoolName}</div>

                    {htmlHeaderText && (
                        <HtmlTextWrapper
                            className={styles.text}
                            htmlText={htmlHeaderText}
                        />
                    )}

                    <DividerHorizontalDesktop
                        marginBottom={0}
                        marginTop={10}
                    />
                    <div className={styles.verticalDatePickerHeader}>
                        <DatePickerVerticalHeader hasNoSpacing={true} />
                    </div>
                </div>
                <div className={styles.datePicker}>
                    <div className={styles.verticalDatePicker}>
                        <DatePickerVertical
                            dateRangeArray={datePickerRange}
                            disabled={disabledDays}
                            hasNoHeader={true}
                            headerTextColor={color('default')}
                            onDayClick={updateSelectedStartDate}
                            selectedDate={selectedDateRange}
                        />
                    </div>
                    <div className={styles.horizontalDatePicker}>
                        <DatePickerHorizontal
                            disabled={disabledDays}
                            fromDate={earliestBookableDate}
                            onDayClick={updateSelectedStartDate}
                            selectedDate={selectedDateRange}
                            toDate={getLastBookableDate()}
                        />
                    </div>
                </div>
                <div className={styles.footer}>
                    <div className={styles.footerBorder} />
                    {selectedDateRange && (
                        <div className={styles.selectedDateSmallOverlay}>
                            <span>{getCtaDateText(selectedDateRange)}</span>
                        </div>
                    )}
                    <div className={styles.ctaWrapper}>
                        <div className={styles.counterWrapper}>
                            <div className={styles.counter}>
                                <CounterDesktop
                                    count={count}
                                    keyboardNavigationContainerId={'school-reservation-overlay-desktop-counter'}
                                    max={countLimits.max}
                                    min={countLimits.min}
                                    onDecrease={handleDayCountDecrease}
                                    onIncrease={handleDayCountIncrease}
                                />
                            </div>
                            {selectedDateRange && (
                                <div className={styles.selectedDate}>
                                    <span>{getCtaDateText(selectedDateRange)}</span>
                                </div>
                            )}
                        </div>
                        <div className={'flex-center-vertical'}>
                            <ButtonWrapper
                                className={styles.buttonWrapper}
                                fontSize={16}
                                fontWeight={'bold'}
                                height={50}
                                isLoading={ctaDisabled}
                                onClick={onCtaClick}
                                qaId={`qa-school-products-booking-overlay-button-add-${
                                    ctaDisabled ? 'disabled' : 'active'
                                }`}
                                width={280}
                            >
                                {!ctaDisabled && (
                                    <div>
                                        zum Warenkorb hinzufügen
                                        <br />({totalPrice})
                                    </div>
                                )}
                            </ButtonWrapper>
                            {cancellation && (
                                <div className={styles.cancellationWrapper}>
                                    <CheckoutCancellationInfoDesktop
                                        iconUrl={cancellation.iconUrl}
                                        overlayHtmlText={cancellation.overlay?.htmlText}
                                        overlayHtmlTitle={cancellation.overlay?.title}
                                        text={cancellation.text}
                                        textColor={cancellation.color}
                                        tippyConfig={{
                                            offset: [0, 10],
                                            placement: 'top',
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}
